import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import keyBy from "lodash/keyBy";

import { api } from "api";
import { routes } from "routes";
import { useSelectedFleet } from "hooks/useSelectedFleet";

import { SingleScreen } from "components/SingleScreen";

import { getLatestDocumentByStatusByType } from "../getLatestDocumentByStatusByType";
import { DocumentType } from "../DocumentType";

import { DocumentForm } from "./DocumentForm";
import {
  destroySnack,
  errorSnack,
  loadingSnack,
  successSnack,
} from "helpers/message";
import { getReadonlyDocument } from "../getReadonlyDocument";

export function VehicleDocumentForm({
  isEditPage,
  documentTypeId,
  documentTypeDetails,
}) {
  const { t } = useTranslation();
  const [documentData, setDocumentData] = useState(null);
  const predefinedDocumentTypeIds = Object.values(DocumentType);
  const isPredefined = predefinedDocumentTypeIds.includes(documentTypeId);

  const { selectedDriverProfile } = useSelectedFleet();

  const {
    driver_vehicles: driverVehiclesOriginal = [],
    owner_vehicles: ownerVehiclesOriginal = [],
    custom_document_type_deadlines: customDocumentTypeDeadlines = [],
  } = selectedDriverProfile;

  const customDocumentTypeDeadlinesByCustomDocumentTypeId = keyBy(
    customDocumentTypeDeadlines,
    "custom_document_type_id",
  );

  /**
   * allVehicles may include duplicates
   * @type {*[]}
   */
  const allVehicles = [...driverVehiclesOriginal, ...ownerVehiclesOriginal];

  const { vehicleId } = useParams();

  const currentVehicle = allVehicles.find(
    (vehicle) => String(vehicle.id) === vehicleId,
  );

  const documents = currentVehicle?.documents;
  const latestDocumentByStatusByType =
    getLatestDocumentByStatusByType(documents);

  const lastDocumentOfCurrentType =
    latestDocumentByStatusByType?.[documentTypeId]?.last;

  // Previous implementation: it was changed because we need to clear form data
  // and set actual data for all documents, not only approved

  /* 
  const currentDocument = isEditPage
    ? lastDocumentOfCurrentType
    : getReadonlyDocument({
        document: lastDocumentOfCurrentType,
        documentSchema: documentTypeDetails,
        context: { currentVehicle, documents },
      });
  */

  useEffect(() => {
    setDocumentData(
      getReadonlyDocument({
        document: lastDocumentOfCurrentType,
        documentSchema: documentTypeDetails,
        context: { currentVehicle, documents },
      }),
    );
  }, [lastDocumentOfCurrentType]);

  const [
    createNewDocumentRequest,
    {
      error: creatingError,
      isLoading: isCreating,
      isSuccess: isCreatingSucceed,
      isError: isCreatingFailed,
      reset,
    },
  ] = api.endpoints.createDocument.useMutation();

  const navigate = useNavigate();

  useEffect(() => {
    if (isCreating) {
      loadingSnack({
        content: t("processing.documentSaving"),
        duration: 0,
        key: "saving_document",
      });
    }
  }, [isCreating, t]);

  useEffect(() => {
    if (isCreatingFailed) {
      const errorMessage = creatingError?.data?.message;

      if (errorMessage === undefined) {
        errorSnack({
          content: t("error.documentNotSaved"),
          duration: 8,
        });
      } else {
        errorSnack({
          content: t("error.documentNotSavedWithReason", {
            reason: errorMessage,
          }),
          duration: 8,
        });
      }
    }

    if (isCreatingSucceed) {
      successSnack({
        content: t("success.documentSaved"),
      });

      navigate(routes.documents.getURL());
    }

    if (isCreatingSucceed || isCreatingFailed) {
      reset();
      destroySnack("saving_document");
    }
  }, [isCreatingSucceed, isCreatingFailed, navigate, reset, creatingError, t]);

  const saveButton = {
    block: true,
    variant: "primary",
    shape: "round",
    fontWeight: "bold",
    onClick: "onSave",
    loading: isCreating,
    disabled: isCreating,
    children: t("action.save"),
  };
  const clearButton = {
    block: true,
    variant: "default",
    importance: "error",
    shape: "round",
    fontWeight: "bold",
    onClick: () => {
      setDocumentData(null);
    },
    children: t("action.clear"),
  };

  const backButton = {
    block: true,
    variant: "secondary",
    shape: "round",
    fontWeight: "bold",
    to: routes.documents.getURL(),
    children: t("action.back"),
  };

  const buttonsIfEditPage = documentData
    ? [clearButton, saveButton, backButton]
    : [saveButton, backButton];

  return (
    <SingleScreen fullHeight>
      <DocumentForm
        isEditPage={isEditPage}
        document={documentData}
        documentType={documentTypeDetails}
        updateBefore={
          documentTypeDetails?.with_deadline &&
          latestDocumentByStatusByType?.[documentTypeId]?.approved ===
            undefined &&
          customDocumentTypeDeadlinesByCustomDocumentTypeId[documentTypeId] !==
            undefined
            ? customDocumentTypeDeadlinesByCustomDocumentTypeId[documentTypeId]
                ?.date
            : undefined
        }
        buttons={
          isEditPage
            ? buttonsIfEditPage
            : [
                {
                  block: true,
                  variant: "primary",
                  shape: "round",
                  fontWeight: "bold",
                  to: routes.documents.getURL(),
                  children: t("action.back"),
                },
              ]
        }
        onSave={({ extraInfo, expireAt, files }) => {
          createNewDocumentRequest({
            type: isPredefined ? documentTypeId : DocumentType.CUSTOM_DOCUMENT,
            customDocumentTypeId: isPredefined ? undefined : documentTypeId,
            extraInfo,
            expireAt,
            files,
            documentableId: currentVehicle.id,
            documentableType: "vehicle",
          });
        }}
      />
    </SingleScreen>
  );
}
